
import { namespace } from 'vuex-class';
import { Component, Emit, Prop, Ref, Watch, Vue } from 'vue-property-decorator';

import GridMetricsSlot from '@/components/virtual-scroller/GridMetricsSlot.vue';
import { waitForHydration, waitForRef } from '@/helpers/async-helper';

interface GridMetrics {
	row: { height?: number; numItems: number };
	gap: number;
}

const Routing = namespace('routing');

@Component({
	name: 'GridVirtualScroller',
	components: { GridMetricsSlot },
})
export default class GridVirtualScroller<Item> extends Vue {
	isMounted = false;

	@Prop({ default: true }) enabled: boolean;
	@Prop() items: Item[];
	@Prop({ type: String, required: true }) gridClass!: string;
	@Prop({ type: String, required: true }) gridItemClass!: string;
	@Prop({ type: Boolean }) gridHasRows!: boolean;

	private destroyed = false;
	private ionTab: HTMLIonTabElement | null = null;
	private gridMetrics: GridMetrics | null = null;
	private boundResizeHandler = () => this.onResize();

	rows: { index: number; items: Item[] }[] = [];

	@Routing.State activeRoute: any;

	@Ref('recycle-scroller') recycleScroller?: any;

	@Emit('update:gridHasRows')
	emitGridHasRows(hasRows: boolean) {
		return hasRows;
	}

	private get isLastRowDirty() {
		if (this.targetNumRows && this.itemsInRow && this.rows.length) {
			const lastRow = this.rows[this.rows.length - 1];
			if (lastRow) {
				const targetLength = this.items.length % this.itemsInRow;
				return this.targetNumRows !== this.numRows || lastRow.items.length !== targetLength;
			}
		}
		return false;
	}

	get itemSize() {
		if (this.gridMetrics && this.gridMetrics.row.height) {
			return this.gridMetrics.row.height + this.gridMetrics.gap;
		} else {
			return 270;
		}
	}

	get hasRows() {
		return this.numRows !== 0;
	}

	get numRows() {
		return this.rows.length;
	}

	get itemsInRow() {
		if (this.gridMetrics) {
			return this.gridMetrics.row.numItems;
		} else {
			return 0;
		}
	}

	get targetNumRows() {
		if (this.itemsInRow) {
			return Math.ceil(this.items.length / this.itemsInRow);
		} else {
			return 0;
		}
	}

	@Watch('activeRoute')
	onActiveRouteChange() {
		if (!this.enabled || this.destroyed) return;
		if (this.ionTab?.active) {
			requestAnimationFrame(() => {
				this.detectGridMetrics();
				this.detectGridRowHeight();
			});
		}
	}

	@Watch('gridMetrics')
	onGridMetricsChange(metrics: GridMetrics | null, oldMetrics: GridMetrics | null) {
		if (!this.enabled || this.destroyed) return;
		if (metrics && oldMetrics) {
			if (metrics.row.numItems !== oldMetrics.row.numItems) {
				this.resetRows();
			}
		}
	}

	@Watch('hasRows')
	onHasRows(hasRows: boolean, oldHasRows: boolean) {
		if (!this.enabled || this.destroyed) return;
		if (hasRows !== oldHasRows) {
			this.emitGridHasRows(hasRows);
		}
	}

	@Watch('items')
	onItemsChange(items: Item[], oldItems?: Item[]) {
		if (!this.enabled || this.destroyed) return;
		// TODO: check if the performance of the resetRows (specially in the watchlist)
		if (oldItems && items.length <= oldItems.length) {
			this.resetRows();
		} else {
			this.updateRows();
		}
	}

	beforeDestroy() {
		// TODO: also remove listener added at `this.setContent` (most likely by a helper that holds all necessary references)
		window.removeEventListener('orientationchange', this.boundResizeHandler);
		window.removeEventListener('resize', this.boundResizeHandler);
		document.removeEventListener('resume', this.boundResizeHandler);
		this.destroyed = true;

		// remove listeners from setContent
		window.removeEventListener('scroll', this.recycleScroller?.handleScroll);
		window.removeEventListener('resize', this.recycleScroller?.handleResize);
	}

	async mounted() {
		this.isMounted = true;

		if (!this.enabled) {
			this.emitGridHasRows(true);
			return;
		}

		// TODO Both ion-content and ion-tab are not present on SSR because of hydration issues.
		//  So we need a workaround. Currently GridVirtualScroller would work only on mobile.
		const content = this.$el.closest('ion-content');
		if (!content) {
			console.error('<GridVirtualScroller> must be used inside an <ion-content>');
			return;
		}

		this.ionTab = this.$el.closest('ion-tab');
		if (!this.ionTab) {
			console.error('<GridVirtualScroller> must be used inside an <ion-tab>');
			return;
		}

		await this.setContent(content);

		window.addEventListener('resize', this.boundResizeHandler);

		this.updateRows();
	}

	updated() {
		this.updateRows();
	}

	private async createRows() {
		if (!this.enabled || this.destroyed) return;
		if (!this.hasRows && !this.gridMetrics) {
			await this.checkGridMetrics();
			if (!this.gridMetrics) return;
		}

		const wasSingleItemRow = this.rows.length === 1 && this.rows[0].items.length === 1;

		for (let i = this.numRows - 1; i < this.targetNumRows; i++) {
			if (i === this.numRows - 1) {
				if (this.isLastRowDirty) {
					this.$set(this.rows[i], 'items', this.getRowItems(i));
				}
			} else if (i >= this.numRows) {
				this.rows.push({ items: this.getRowItems(i), index: i });
			}
		}

		if (this.gridMetrics) {
			this.$nextTick(() => {
				if (wasSingleItemRow && this.items.length > 1) {
					this.detectGridMetrics();
				} else {
					this.detectGridRowHeight();
				}
			});
		}
	}

	private detectGridMetrics() {
		if (!this.enabled || this.destroyed) return;
		const gridElement = this.$el.querySelector(`.${this.gridClass}`);

		if (gridElement) {
			const gridRect = gridElement.getBoundingClientRect();
			if (!gridRect.width) return;

			const itemElements = gridElement.querySelectorAll(
				`.${this.gridItemClass}:nth-child(1), .${this.gridItemClass}:nth-child(2)`
			);

			if (itemElements.length === 1) {
				// TODO: Using gap `0` might lead to edge cases where the switch between on item and two items won't work;
				const item1Rect = itemElements[0].getBoundingClientRect();
				const numItems = item1Rect.width ? Math.round(gridRect.width / item1Rect.width) : 0;
				if (numItems) {
					this.gridMetrics = {
						gap: 0,
						row: {
							height: item1Rect.height,
							numItems,
						},
					};
				}
			}
			if (itemElements.length === 2) {
				const item1Rect = itemElements[0].getBoundingClientRect();
				const item2Rect = itemElements[1].getBoundingClientRect();

				const totalidth = item2Rect.left - item1Rect.left;
				const numItems = totalidth ? Math.round(gridRect.width / totalidth) : 1;
				const gap = totalidth ? item2Rect.left - item1Rect.right : 0;
				if (numItems) {
					this.gridMetrics = {
						gap,
						row: {
							height: item1Rect.height,
							numItems,
						},
					};
				}
			}
		}
	}

	private detectGridRowHeight() {
		if (!this.enabled || this.destroyed) return;
		if (this.gridMetrics) {
			const itemElement = this.$el.querySelector(`.${this.gridItemClass}`);

			if (itemElement) {
				const itemRect = itemElement.getBoundingClientRect();
				if (itemRect.height && itemRect.height !== this.gridMetrics.row.height) {
					this.$set(this.gridMetrics, 'row', { ...this.gridMetrics.row, height: itemRect.height });
				}
			}
		}
	}

	private getRowItems(row: number) {
		const start = row * this.itemsInRow;
		const end = start + this.itemsInRow;
		return this.items.slice(start, end);
	}

	private onResize() {
		this.detectGridMetrics();
	}

	checkGridMetrics(reset = false, tries = 0, resolve: (() => void) | null = null) {
		let promise: Promise<void> | undefined;
		if (!resolve) {
			promise = new Promise(r => {
				resolve = r;
			});
		}

		if (reset) {
			this.gridMetrics = null;
		}

		this.detectGridMetrics();
		if (!this.gridMetrics) {
			if (tries < 3) {
				requestAnimationFrame(() => this.checkGridMetrics(false, ++tries, resolve));
			} else {
				console.warn('No valid grid metrics detected.');
			}
		} else {
			resolve!();
		}

		return promise;
	}

	updateRows() {
		if (!this.enabled || this.destroyed) return;
		if (this.items.length && !this.hasRows) {
			this.createRows();
			return;
		}

		if (this.hasRows && !this.items.length) {
			this.resetRows();
			return;
		}

		if (this.targetNumRows < this.numRows) {
			this.resetRows();
			return;
		}

		if (this.isLastRowDirty) {
			this.createRows();
			return;
		}
	}

	resetRows() {
		this.rows = [];
		this.createRows();
	}

	async setContent(content: any) {
		if (!this.enabled || this.destroyed) return;
		await waitForHydration(content);
		const scrollElement = await content.getScrollElement();
		await waitForRef(this, 'recycle-scroller');

		if (this.recycleScroller) {
			this.recycleScroller.listenerTarget = scrollElement;

			// TODO: investigate possibility of destroying these listeners on destroyed (if it ever gets destroyed at all)
			scrollElement.addEventListener('scroll', this.recycleScroller.handleScroll);
			scrollElement.addEventListener('resize', this.recycleScroller.handleResize);
		}
	}
}
